import { K } from '../kabinet/atoms';

/**
 * Скелеты загрузки.
 *
 * ЗАЧЕМ ОНИ, ЕСЛИ ЕСТЬ СЛОВО «ЧИТАЮ». Слово отвечает на вопрос «идёт ли
 * что-нибудь», но не отвечает на главный: «сколько ждать и что придёт».
 * А ещё оно занимает одну строку — и когда данные приходят, экран
 * прыгает: строка исчезает, на её место встают восемнадцать столбцов,
 * и рука, уже занесённая над кнопкой, попадает не туда.
 *
 * СКЕЛЕТ ЗАНИМАЕТ РОВНО ТО МЕСТО, КОТОРОЕ ЗАЙМУТ ДАННЫЕ. Ничего
 * не прыгает, и по форме заранее видно, что грузится: таблица кампаний,
 * а не карточка и не список.
 *
 * ДВИЖЕНИЕ ОТКЛЮЧАЕТСЯ ВМЕСТЕ СО ВСЕМИ: `prefers-reduced-motion`
 * гасит перелив в `styles.css`, и скелет остаётся спокойным серым —
 * он и без движения делает свою работу.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ГРАДИЕНТ ПРИХОДИТ ИЗ `rk-skeleton`, А НЕ ИЗ ЧАСТИЦЫ, И ЭТО НЕ ОБХОД.
 *
 * Эталон пишет перелив краткой записью: `background: linear-gradient(…)`
 * с токенами внутри. Браузер, отдавая `style` обратно, РАСКЛАДЫВАЕТ её
 * на длинные имена и оставляет их пустыми (`background-image: ;`) —
 * значение с `var()` он сериализовать не умеет. Сборщик частиц пустые
 * объявления выбрасывает (и правильно делает: пустое значение не станет
 * ничем), поэтому в `k-значок-грузится` приходят размер, скругление
 * и движение — но не сама краска.
 *
 * `.rk-skeleton` в `styles.css` держит тот же градиент из тех же
 * токенов побайтово. Частица даёт размеры эталона, класс — краску;
 * `background-size` и `animation` у них совпадают, спорить нечему.
 *
 * Потерю краткой записи сторожит `kabinet/atoms.spec.ts`: правило
 * с `background-size` и без краски — это выпавший градиент, а не выбор.
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ШЕСТЬ СТРОК С РАЗНОЙ ДЛИНОЙ — ЧИСЛА ЭТАЛОНА, А НЕ ПОХОЖИЕ НА НИХ.
 *
 * До 27.08 скелет рисовал шапку таблицы и восемь одинаковых строк
 * из шести столбиков — своя выдумка, похожая на таблицу. Эталон рисует
 * другое: шесть строк «квадрат 32 · длинная полоса · короткий хвост
 * справа», и длины у всех шести разные.
 *
 * ДЛИНА ЗДЕСЬ — СОДЕРЖИМОЕ. В эталоне она приходит из данных
 * (`width: {{ s.w }}`), поэтому частица даёт числа первой строки,
 * а разметка перебивает их своими. Ровный частокол одинаковых полос
 * читается как узор, а не как данные, которых ещё нет.
 * ═══════════════════════════════════════════════════════════════════════
 */
const СТРОКИ = [
  { имя: '34%', хвост: '12%' },
  { имя: '46%', хвост: '9%' },
  { имя: '28%', хвост: '14%' },
  { имя: '52%', хвост: '10%' },
  { имя: '38%', хвост: '13%' },
  { имя: '44%', хвост: '8%' },
] as const;

export function SkeletonTable({
  rows = СТРОКИ.length,
  имя,
  aria = 'Загружаю кампании',
}: {
  rows?: number;
  /**
   * Имя узла для сверки. Стоит ТОЛЬКО у скелета биддера: эталон рисует
   * `состояние-грузится` на одном экране, и второй узел с тем же именем
   * на другом экране сверка прочла бы как нашу самодеятельность.
   * Остальные три места (журнал, история ставок, карточка кампании)
   * берут тот же рисунок безымянными.
   */
  имя?: string;
  aria?: string;
}) {
  return (
    <div
      data-d={имя}
      className={K.состояниеГрузится}
      role="status"
      aria-busy="true"
      aria-label={aria}
    >
      {Array.from({ length: rows }, (_, i) => СТРОКИ[i % СТРОКИ.length]!).map((с, i) => (
        <div key={i} className={K.рядГрузится}>
          <span className={`rk-skeleton ${K.значокГрузится}`} aria-hidden />
          <span className={`rk-skeleton ${K.полосаГрузится}`} style={{ width: с.имя }} aria-hidden />
          <span
            className={`rk-skeleton ${K.хвостГрузится}`}
            style={{ width: с.хвост }}
            aria-hidden
          />
        </div>
      ))}
    </div>
  );
}

/** Полоса скелета. Размеры в пикселях: это макет, а не содержимое. */
function Bar({ w, h = 12 }: { w: number | string; h?: number }) {
  return <span className="rk-skeleton block" style={{ width: w, height: h }} aria-hidden />;
}

/**
 * Карточка кампании.
 *
 * ЗАМЕНЯЕТ СОБОЙ НЕ ПУСТОТУ, А НЕВЕРНЫЙ ОТВЕТ. Пока список кампаний
 * не пришёл, карточка не знает своей кампании — и до этого скелета
 * успевала написать «Кампания не найдена», а через миг перерисоваться.
 * Долю секунды человек читает готовый ответ, и ответ этот неверный:
 * «ещё не знаем» и «нет такой» — разные вещи, как и везде в кабинете.
 */
export function SkeletonCard() {
  return (
    <div
      className="flex flex-col"
      role="status"
      aria-busy="true"
      aria-label="Загружаю кампанию"
      style={{ gap: 'var(--sp-4)', padding: 'var(--sp-5)' }}
    >
      <Bar w={120} h={10} />

      <div
        className="flex flex-wrap items-start gap-5"
        style={{
          background: 'var(--bg-surface)',
          border: 'var(--hair) solid var(--line)',
          borderLeft: '3px solid var(--line-strong)',
          borderRadius: 'var(--r-lg)',
          padding: 'var(--sp-5)',
        }}
      >
        <Bar w={40} h={40} />
        <span className="flex flex-col gap-2">
          <Bar w={260} h={16} />
          <Bar w={180} h={10} />
        </span>
        <span className="ml-auto flex gap-2">
          <Bar w={92} h={22} />
          <Bar w={92} h={22} />
        </span>
      </div>

      {[0, 1].map((i) => (
        <div key={i} className="rk-section flex flex-col gap-3">
          <Bar w={160} h={12} />
          <Bar w="100%" h={10} />
          <Bar w="70%" h={10} />
        </div>
      ))}
    </div>
  );
}
